import React, { useRef, useState, useEffect } from 'react';
import { SignatureMethod } from '../../types';
import { Camera, RefreshCw, CheckCircle, PenTool, Lock, AlertTriangle, Maximize2, X } from 'lucide-react';

interface SignatureInputProps {
  method: SignatureMethod;
  value: any;
  onChange: (val: any) => void;
  allowRefusal: boolean;
  onRefusal: (reason: string) => void;
  label: string;
}

const SignatureInput: React.FC<SignatureInputProps> = ({ 
  method, value, onChange, allowRefusal, onRefusal, label 
}) => {
  const [isRefusing, setIsRefusing] = useState(false);
  const [refusalText, setRefusalText] = useState('');
  
  // Canvas Refs
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [isFullscreenSign, setIsFullscreenSign] = useState(false);
  const [canvasDimensions, setCanvasDimensions] = useState({ width: 300, height: 180 });

  useEffect(() => {
    const updateDims = () => {
       if (isFullscreenSign) {
          setCanvasDimensions({ width: window.innerWidth - 40, height: window.innerHeight - 100 });
       } else {
          // You might want to also adjust the normal width based on the container, but 300 is the default.
          // Let's ensure it's slightly responsive if screen is very small
          const w = Math.min(window.innerWidth - 40, 300);
          setCanvasDimensions({ width: w, height: 180 });
       }
    };
    updateDims();
    window.addEventListener('resize', updateDims);
    return () => window.removeEventListener('resize', updateDims);
  }, [isFullscreenSign]);

  // Video Refs
  const videoRef = useRef<HTMLVideoElement>(null);
  const [cameraActive, setCameraActive] = useState(false);
  const [stream, setStream] = useState<MediaStream | null>(null);

  // Password State
  const [password, setPassword] = useState('');

  // --- Handlers ---

  const handleRefusalToggle = () => {
    setIsRefusing(!isRefusing);
    if (!isRefusing) {
      onChange(null);
      // Clean up camera if active
      stopCamera();
      setPassword('');
    }
  };

  const confirmRefusal = () => {
    if (refusalText.trim().length > 0) {
      onRefusal(refusalText);
    }
  };

  // Handwritten Logic
  useEffect(() => {
    if (method === SignatureMethod.HANDWRITTEN && canvasRef.current && !isRefusing) {
      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');
      if (ctx) {
        ctx.lineWidth = 3;
        ctx.lineCap = 'round';
        ctx.strokeStyle = '#000';
      }
    }
  }, [method, isRefusing, isFullscreenSign]);

  const startDrawing = (e: React.MouseEvent | React.TouchEvent) => {
    setIsDrawing(true);
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    const x = ('touches' in e ? e.touches[0].clientX : e.clientX) - rect.left;
    const y = ('touches' in e ? e.touches[0].clientY : e.clientY) - rect.top;
    ctx?.beginPath();
    ctx?.moveTo(x, y);
  };

  const draw = (e: React.MouseEvent | React.TouchEvent) => {
    if (!isDrawing) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    const x = ('touches' in e ? e.touches[0].clientX : e.clientX) - rect.left;
    const y = ('touches' in e ? e.touches[0].clientY : e.clientY) - rect.top;
    ctx?.lineTo(x, y);
    ctx?.stroke();
  };

  const endDrawing = () => {
    setIsDrawing(false);
    if (canvasRef.current) {
      onChange(canvasRef.current.toDataURL());
    }
  };

  // --- Selfie Logic Fixed ---
  
  const startCamera = async () => {
    try {
      const mediaStream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: "user" } });
      setStream(mediaStream);
      setCameraActive(true);
    } catch (err) {
      console.error("Camera error:", err);
      alert("Erro ao acessar a câmera. Verifique as permissões.");
    }
  };

  const stopCamera = () => {
    if (stream) {
      stream.getTracks().forEach(track => track.stop());
      setStream(null);
    }
    setCameraActive(false);
  };

  // Attach stream to video element when ready
  useEffect(() => {
    if (cameraActive && videoRef.current && stream) {
      videoRef.current.srcObject = stream;
    }
    return () => {
      // Cleanup on unmount if camera was active
      if (!cameraActive && stream) {
         stream.getTracks().forEach(t => t.stop());
      }
    };
  }, [cameraActive, stream]);

  const captureSelfie = () => {
    if (videoRef.current) {
      const canvas = document.createElement('canvas');
      canvas.width = videoRef.current.videoWidth;
      canvas.height = videoRef.current.videoHeight;
      const ctx = canvas.getContext('2d');
      
      // Flip horizontally for selfie mirror effect fix
      ctx?.translate(canvas.width, 0);
      ctx?.scale(-1, 1);
      
      ctx?.drawImage(videoRef.current, 0, 0);
      
      const dataUrl = canvas.toDataURL('image/jpeg');
      onChange(dataUrl);
      stopCamera();
    }
  };

  const retakeSelfie = () => {
    onChange(null);
    startCamera();
  };

  // --- Render ---

  if (isRefusing) {
    return (
      <div className="border border-red-200 bg-red-50 p-4 rounded-xl animate-in fade-in">
        <div className="flex justify-between items-center mb-2">
          <h4 className="text-red-700 font-bold flex items-center gap-2">
            <AlertTriangle size={20} /> Recusa de Assinatura
          </h4>
          <button onClick={handleRefusalToggle} className="text-sm font-medium text-slate-500 hover:text-slate-800">Cancelar</button>
        </div>
        <p className="text-sm text-red-600 mb-2">Obrigatório: Por favor, explique o motivo da recusa.</p>
        <textarea 
          className="w-full p-3 border border-red-300 rounded-lg focus:ring-red-500 focus:border-red-500 text-lg"
          rows={3}
          value={refusalText}
          onChange={(e) => setRefusalText(e.target.value)}
          placeholder="Não posso assinar porque..."
        />
        <button 
          onClick={confirmRefusal}
          disabled={!refusalText}
          className="mt-3 w-full bg-red-600 text-white px-4 py-3 rounded-lg font-bold hover:bg-red-700 disabled:opacity-50"
        >
          Confirmar Recusa
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex justify-between items-end px-1">
        <label className="block text-lg font-bold text-slate-800">{label}</label>
        {allowRefusal && (
          <button 
            type="button"
            onClick={handleRefusalToggle} 
            className="text-xs font-semibold text-red-500 hover:text-red-700 bg-red-50 px-2 py-1 rounded"
          >
            Recusar
          </button>
        )}
      </div>

      <div className="border rounded-2xl p-1 bg-white shadow-sm overflow-hidden">
        
        {/* HANDWRITTEN */}
        {method === SignatureMethod.HANDWRITTEN && (
          <div className={`relative bg-slate-50 rounded-xl ${isFullscreenSign ? 'fixed inset-0 z-50 flex flex-col items-center justify-center bg-white p-4' : ''}`}>
            {isFullscreenSign && (
                 <div className="absolute top-4 left-4 text-slate-400 rotate-90 origin-left text-xs uppercase tracking-widest">Modo Paisagem</div>
            )}
            
            <div className="absolute top-2 right-2 z-10">
                <button 
                    type="button" 
                    onClick={() => setIsFullscreenSign(!isFullscreenSign)}
                    className="p-2 bg-white rounded-full shadow text-slate-600"
                >
                    {isFullscreenSign ? <CheckCircle className="text-green-600"/> : <Maximize2 size={16}/>}
                </button>
            </div>

            <canvas
              ref={canvasRef}
              width={canvasDimensions.width}
              height={canvasDimensions.height}
              style={{ touchAction: 'none' }}
              className="touch-none w-full bg-slate-50 cursor-crosshair rounded-xl"
              onMouseDown={startDrawing}
              onMouseMove={draw}
              onMouseUp={endDrawing}
              onMouseLeave={endDrawing}
              onTouchStart={startDrawing}
              onTouchMove={draw}
              onTouchEnd={endDrawing}
            />
            
            {!isFullscreenSign && (
                <div className="absolute bottom-2 left-2 text-xs text-slate-400 pointer-events-none flex items-center gap-1">
                    <PenTool size={12} /> Assine Aqui
                </div>
            )}

            <button 
              type="button"
              className="absolute bottom-2 right-2 text-xs text-blue-600 font-medium px-2 py-1 bg-blue-50 rounded"
              onClick={() => {
                const ctx = canvasRef.current?.getContext('2d');
                ctx?.clearRect(0,0, canvasRef.current?.width || 300, canvasRef.current?.height || 180);
                onChange(null);
              }}
            >
              Limpar
            </button>
          </div>
        )}

        {/* SELFIE */}
        {method === SignatureMethod.BIOMETRIC_VISUAL && (
          <div className="flex flex-col items-center justify-center min-h-[250px] bg-slate-100 rounded-xl relative overflow-hidden">
            {!cameraActive && !value && (
              <button 
                type="button"
                onClick={startCamera}
                className="flex flex-col items-center text-indigo-600 hover:text-indigo-800 p-8"
              >
                <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center shadow mb-3">
                     <Camera size={32} />
                </div>
                <span className="font-bold">Tirar Selfie Biométrica</span>
                <span className="text-xs text-slate-400 mt-1">Validação facial necessária</span>
              </button>
            )}
            
            {cameraActive && (
              <div className="relative w-full h-full flex justify-center bg-black">
                <video 
                    ref={videoRef} 
                    autoPlay 
                    playsInline 
                    muted 
                    className="w-full h-[300px] object-cover opacity-90 scale-x-[-1]" 
                />
                
                {/* Silhouette Overlay */}
                <div className="absolute inset-0 pointer-events-none flex items-center justify-center z-10">
                    <div className="w-40 h-56 border-4 border-white/50 rounded-[50%] shadow-[0_0_0_999px_rgba(0,0,0,0.5)]"></div>
                    <div className="absolute top-4 text-white font-bold bg-black/50 px-3 py-1 rounded-full text-sm">
                        Posicione o rosto no oval
                    </div>
                </div>
                
                {/* Cancel Camera Button */}
                <button 
                  type="button"
                  onClick={stopCamera}
                  className="absolute top-4 right-4 z-20 bg-black/50 text-white p-2 rounded-full"
                >
                  <X size={20} />
                </button>

                <button 
                  type="button"
                  onClick={captureSelfie}
                  className="absolute bottom-6 left-1/2 transform -translate-x-1/2 z-20 bg-white rounded-full p-1 shadow-xl active:scale-95 transition-transform"
                >
                  <div className="w-14 h-14 border-4 border-slate-100 rounded-full flex items-center justify-center">
                       <div className="w-10 h-10 bg-red-500 rounded-full"></div>
                  </div>
                </button>
              </div>
            )}

            {value && (
              <div className="relative w-full animate-in fade-in">
                <img src={value} alt="Signature Selfie" className="w-full h-[300px] object-cover" />
                <div className="absolute inset-0 bg-green-500/10 flex items-center justify-center">
                    <div className="bg-white/90 backdrop-blur px-4 py-2 rounded-full flex items-center gap-2 text-green-700 font-bold shadow-lg">
                        <CheckCircle size={20} /> Verificado
                    </div>
                </div>
                <button 
                  type="button"
                  onClick={retakeSelfie}
                  className="absolute bottom-4 right-4 bg-white p-2 rounded-full shadow-lg text-slate-700 hover:bg-slate-50 font-medium text-sm flex items-center gap-2 px-4 z-20"
                >
                  <RefreshCw size={16}/> Tirar Outra
                </button>
              </div>
            )}
          </div>
        )}

        {/* CREDENTIAL / PASSWORD */}
        {method === SignatureMethod.CREDENTIAL && (
          <div className="p-6">
            <div className="flex items-center gap-2 mb-3 text-sm font-semibold text-slate-500">
               <Lock size={16} /> Digite a Senha do Operador
            </div>
            <input
              type="password"
              className="w-full bg-slate-50 border-0 rounded-xl p-4 text-xl tracking-widest focus:ring-2 focus:ring-indigo-500"
              placeholder="••••••••"
              value={password}
              onChange={(e) => {
                setPassword(e.target.value);
                if (e.target.value.length > 3) {
                    onChange('valid_token_mock');
                } else {
                    onChange(null);
                }
              }}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default SignatureInput;